// Tracks: defined by control points (x, z, altitude), sampled every metre.
import { BRAKE, cornerSpeed, laneFactor } from './physics.js';

export const LANE_W = 2.4;
/**
 * Sideways limit of a rider's position (road units of LANE_W, either side of the centre line): about 4.1 m, a hand short
 * of the 4.8 m edge. Nothing but this limit bounds a position: there are no lanes on the road, anyone rides anywhere.
 */
export const ROAD_EDGE = 1.7;
/** Top sideways speed (units per second): about 1.6 m/s, a determined move. An instant swerve would shed any wheel for free. */
export const LANE_SPEED = 0.65;
/**
 * A rider steers a bicycle, not a cursor: the sideways speed builds up and dies away at a bounded acceleration, and it
 * scales with how far the aim is. Small corrections are slow, a real move goes up to LANE_SPEED.
 * `calm`: speed of the smallest correction. `gain`: speed per unit of distance to the aim (`botGain`: a bot in no hurry). `accel`: units/s².
 */
export const LATERAL = { calm: 0.05, gain: 1.6, botGain: 0.6, accel: 1.2, angle: { bot: 0.08, human: 0.15 }, floor: 0.06 };
/**
 * Fastest a rider of speed `v` (m/s) moves across the road, in units/s. A bicycle's path turns by a few degrees, not across
 * the road: about 4.5 degrees for a bot, 8.5 for a human (`LATERAL.angle`, tangents), whatever the speed; on a climb a
 * move simply takes longer. `floor`: a rider at walking pace can still move over.
 */
export const lateralLimit = (v: number, human: boolean): number => Math.min(LANE_SPEED, Math.max(LATERAL.floor, (v * (human ? LATERAL.angle.human : LATERAL.angle.bot)) / LANE_W));
/** One step of a rider's sideways position towards `aim`: the new position and sideways speed. */
export function lateralStep(lane: number, speed: number, aim: number, dt: number, gain = LATERAL.gain, top = LANE_SPEED): { lane: number; speed: number } {
  const err = aim - lane, want = Math.sign(err) * Math.min(top, LATERAL.calm + Math.abs(err) * gain) * Math.min(1, Math.abs(err) / 0.02);
  // never faster than what can still be stopped by the aim at this acceleration: the rider settles, with no overshoot
  const stop = Math.sqrt(2 * LATERAL.accel * Math.abs(err)), limited = Math.sign(want) * Math.min(Math.abs(want), stop);
  const dv = limited - speed, next = speed + Math.sign(dv) * Math.min(Math.abs(dv), LATERAL.accel * dt);
  if (Math.abs(err) < 1e-3 && Math.abs(next) < 0.02) return { lane: aim, speed: 0 };
  return { lane: lane + next * dt, speed: next };
}

/** Track: control points [x, z, altitude] in metres, closed into a loop. No `id`: identifier derived from the points. */
export interface TrackDef {
  id?: string; name: string; desc?: string; level?: number; pts: number[][];
  /** Real altitude of the lowest point (m): air density. Absent: sea level. */
  alt0?: number;
}
export interface Track {
  id: string; name: string; N: number; step: number; L: number;
  x: Float32Array; z: Float32Array; y: Float32Array; tx: Float32Array; tz: Float32Array; grade: Float32Array;
  /** Signed curvature (1/m), positive when the corner's centre is on the side of the positive lanes. */
  curv: Float32Array;
  /** Allowed speed (m/s): corner limit, lowered upstream to leave braking distance. */
  vmax: Float32Array;
  /** Real altitude of the lowest point (m). */
  alt0: number;
  climb: number; boxRows: number[];
}
export interface TrackPoint { x: number; y: number; z: number; tx: number; tz: number; grade: number }

export const TRACKS: Record<string, TrackDef> = {
  collines: {
    name: 'Hills Circuit',
    desc: '2.7 km · a 7% climb then a false flat',
    level: 1,
    pts: [[0, 0, 0], [250, -20, 2], [480, 40, 10], [620, 200, 26], [600, 420, 34], [430, 560, 24], [200, 580, 12], [20, 480, 6], [-150, 560, 14], [-330, 450, 22], [-380, 230, 12], [-250, 60, 4]],
  },
  mur: {
    name: 'Wall Loop',
    desc: '2.1 km · fast and flat with a short 6% wall',
    level: 1,
    pts: [[0, 0, 0], [300, 0, 0], [480, 80, 1], [540, 250, 2], [460, 400, 3], [280, 440, 14], [100, 420, 14], [-80, 400, 4], [-220, 300, 1], [-250, 130, 0], [-160, 20, 0]],
  },
  nuages: {
    name: 'Cloud Pass',
    desc: '3.2 km · a real 90 m climb, gradients up to 10%',
    level: 3,
    pts: [[0, 0, 0], [300, 0, 4], [600, 80, 20], [800, 250, 42], [790, 480, 64], [600, 620, 86], [350, 650, 92], [100, 700, 82], [-150, 650, 66], [-350, 520, 48], [-420, 320, 30], [-360, 140, 14], [-200, 30, 4]],
  },
  crit: {
    name: 'City Criterium',
    desc: '1.6 km · flat, tight corners, perfect for sprinters',
    level: 6,
        // the point at 80 m rounds the last corner: without it the spline makes a 6 m radius bend before the line
    pts: [[0, 0, 0], [80, 0, 0], [350, 0, 0], [448, 42, 0], [476, 168, 1], [448, 308, 1], [336, 364, 0], [84, 364, 0], [-28, 308, 0], [-56, 168, 0], [-40, 40, 0]],
  },
};

export const mod = (a: number, n: number): number => ((a % n) + n) % n;

function catmull(p0: number[], p1: number[], p2: number[], p3: number[], t: number): number[] {
  const t2 = t * t, t3 = t2 * t;
  return p1.map((_, k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3));
}

const cache = new Map<string, Track>();
const CACHE_MAX = 24;

/** Short, stable fingerprint of the points (identifier of a created track without an id). */
export function trackKey(pts: number[][]): string {
  let h = 0x811c9dc5;
  for (const p of pts) for (const v of p) { h ^= Math.round(v * 10); h = Math.imul(h, 0x01000193); }
  return 'c' + (h >>> 0).toString(36);
}

/** Builds (and caches) a sampled track: positions, tangents, smoothed gradients. */
export function buildTrack(src: string | TrackDef): Track {
  const def = typeof src === 'string' ? TRACKS[src] || TRACKS.collines : src;
  const id = typeof src === 'string' ? (TRACKS[src] ? src : 'collines') : src.id || trackKey(src.pts);
  const key = typeof src === 'string' ? id : `${id}|${trackKey(src.pts)}|${src.alt0 ?? 0}`; // two versions of a creation do not share the cache
  const hit = cache.get(key);
  if (hit) return hit;
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value!);
  const P = def.pts, n = P.length;
  const dense: number[][] = [];
  for (let i = 0; i < n; i++) for (let k = 0; k < 120; k++) dense.push(catmull(P[mod(i - 1, n)], P[i], P[(i + 1) % n], P[(i + 2) % n], k / 120));
  const cum = [0];
  for (let i = 1; i <= dense.length; i++) {
    const a = dense[i - 1], b = dense[i % dense.length];
    cum.push(cum[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = cum[cum.length - 1], N = Math.round(total), step = total / N;
  const x = new Float32Array(N), z = new Float32Array(N), y = new Float32Array(N);
  let j = 0;
  for (let i = 0; i < N; i++) {
    const s = i * step;
    while (cum[j + 1] < s) j++;
    const f = (s - cum[j]) / (cum[j + 1] - cum[j] || 1), a = dense[j], b = dense[(j + 1) % dense.length];
    x[i] = a[0] + (b[0] - a[0]) * f; z[i] = a[1] + (b[1] - a[1]) * f; y[i] = a[2] + (b[2] - a[2]) * f;
  }
  const ys = new Float32Array(N), W = 25;
  for (let i = 0; i < N; i++) { let acc = 0; for (let k = -W; k <= W; k++) acc += y[mod(i + k, N)]; ys[i] = acc / (2 * W + 1); }
  const tx = new Float32Array(N), tz = new Float32Array(N), grade = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const a = mod(i - 3, N), b = (i + 3) % N, dx = x[b] - x[a], dz = z[b] - z[a], h = Math.hypot(dx, dz) || 1;
    tx[i] = dx / h; tz[i] = dz / h; grade[i] = (ys[b] - ys[a]) / h;
  }
  let climb = 0;
  for (let i = 0; i < N; i++) climb += Math.max(0, ys[(i + 1) % N] - ys[i]);
  const L = N * step, curv = curvature(tx, tz, N, step);
  const tr: Track = { id, name: def.name, N, step, L, x, z, y: ys, tx, tz, grade, curv, vmax: speedLimits(curv, step), alt0: def.alt0 ?? 0, climb, boxRows: [0.12, 0.42, 0.72].map(f => Math.round(f * L)) };
  cache.set(key, tr);
  return tr;
}

/** Signed curvature measured between tangents ±4 steps apart (smooths out small sampling glitches). */
function curvature(tx: Float32Array, tz: Float32Array, N: number, step: number): Float32Array {
  const c = new Float32Array(N), K = 4;
  for (let i = 0; i < N; i++) {
    const a = mod(i - K, N), b = (i + K) % N;
    c[i] = Math.atan2(tx[a] * tz[b] - tz[a] * tx[b], tx[a] * tx[b] + tz[a] * tz[b]) / (2 * K * step);
  }
  return c;
}

/** Allowed speed with braking anticipation. */
function speedLimits(curv: Float32Array, step: number): Float32Array {
  const N = curv.length, v = new Float32Array(N);
  for (let i = 0; i < N; i++) v[i] = cornerSpeed(Math.abs(curv[i]) > 1e-6 ? 1 / Math.abs(curv[i]) : Infinity);
  // backward pass, two laps to cross the line: v² ≤ v_next² + 2·BRAKE·step
  for (let n = 2 * N - 1; n >= 0; n--) {
    const i = n % N, next = v[(i + 1) % N];
    v[i] = Math.min(v[i], Math.sqrt(next * next + 2 * BRAKE * step));
  }
  return v;
}

/** Allowed speed (m/s) at distance s: the lower of the two surrounding samples. On a lane shifted
 * by `offset` m, the radius changes: the limit follows the square root of the chord factor. */
export function speedLimitAt(tr: Track, s: number, offset = 0): number {
  const i = Math.floor(mod(s, tr.L) / tr.step) % tr.N;
  const v = Math.min(tr.vmax[i], tr.vmax[(i + 1) % tr.N]);
  return offset ? v * Math.sqrt(laneFactor(tr.curv[i], offset)) : v;
}

/** Chord factor at distance s for a lane shifted by `offset` m (see laneFactor). */
export function laneFactorAt(tr: Track, s: number, offset: number): number {
  return laneFactor(tr.curv[Math.floor(mod(s, tr.L) / tr.step) % tr.N], offset);
}

/** Position, tangent and gradient at distance s (m), shifted sideways by `lane` lanes. */
export function trackAt(tr: Track, s: number, lane = 0): TrackPoint {
  const u = mod(s, tr.L) / tr.step, i = Math.floor(u) % tr.N, k = (i + 1) % tr.N, f = u - Math.floor(u);
  const lx = tr.x[i] + (tr.x[k] - tr.x[i]) * f, lz = tr.z[i] + (tr.z[k] - tr.z[i]) * f, ly = tr.y[i] + (tr.y[k] - tr.y[i]) * f;
  const tx = tr.tx[i] + (tr.tx[k] - tr.tx[i]) * f, tz = tr.tz[i] + (tr.tz[k] - tr.tz[i]) * f, h = Math.hypot(tx, tz) || 1;
  return { x: lx - (tz / h) * lane * LANE_W, y: ly, z: lz + (tx / h) * lane * LANE_W, tx: tx / h, tz: tz / h, grade: tr.grade[i] + (tr.grade[k] - tr.grade[i]) * f };
}

export const altitudeAt = (tr: Track, s: number): number => trackAt(tr, s).y;

/** Key figures of a track (cards, previews, validation). */
export function trackFacts(tr: Track) {
  let maxGrade = 0, minGrade = 0;
  for (let i = 0; i < tr.N; i++) { if (tr.grade[i] > maxGrade) maxGrade = tr.grade[i]; if (tr.grade[i] < minGrade) minGrade = tr.grade[i]; }
  return { length: tr.L, climb: tr.climb, maxGrade, minGrade };
}

// ---------- Tracks described by segments ----------
// A human (or the AI) thinks in lengths, corners and gradients, not coordinates. The course is built
// turtle-style then closed: the heading, position and altitude gaps are spread along the route.

/**
 * Most photogenic distance along a track, for its thumbnail: the heart of the steepest climb (gradient smoothed over
 * about forty metres), or failing that the tightest corner. The start arch and the finish line are avoided.
 */
export function scenicSpot(tr: Track): number {
  const w = Math.max(1, Math.round(20 / tr.step));
  let climb = -Infinity, climbS = -1, bend = -Infinity, bendS = -1;
  for (let i = 0; i < tr.N; i++) {
    const s = i * tr.step;
    if (s < 80 || s > tr.L - 40) continue;
    let g = 0, c = 0;
    for (let k = -w; k <= w; k++) { const j = mod(i + k, tr.N); g += tr.grade[j]; c += Math.abs(tr.curv[j]); }
    if (g > climb) { climb = g; climbS = s; }
    if (c > bend) { bend = c; bendS = s; }
  }
  if (climbS < 0) return mod(tr.L * 0.3, tr.L); // track too short to avoid the start
  return climb / (2 * w + 1) >= 0.025 ? climbS : bendS;
}

/** Segment: length (m), total turn over the segment (degrees, + to the right), average gradient (%). */
export interface CourseSegment { len: number; turn: number; grade: number }
export interface CourseIssue { level: 'error' | 'warning'; message: string }

const STEP = 20;

/** Control points [x, z, altitude] of a loop described by segments. */
export function courseToPoints(segs: CourseSegment[]): number[][] {
  const steps: { d: number; turn: number; dz: number }[] = [];
  for (const g of segs) {
    const n = Math.max(1, Math.round(g.len / STEP));
    for (let i = 0; i < n; i++) steps.push({ d: g.len / n, turn: (g.turn * Math.PI) / 180 / n, dz: ((g.grade / 100) * g.len) / n });
  }
  if (!steps.length) return [];
  const L = steps.reduce((a, s) => a + s.d, 0);
  // heading closure: the sum of turns is brought back to the nearest full turn
  const total = steps.reduce((a, s) => a + s.turn, 0), full = 2 * Math.PI * (Math.round(total / (2 * Math.PI)) || Math.sign(total) || 1);
  const dTurn = (full - total) / L, dAlt = -steps.reduce((a, s) => a + s.dz, 0) / L;
  let x = 0, z = 0, alt = 0, h = 0, s = 0;
  const raw: number[][] = [[0, 0, 0, 0]];
  for (const st of steps) {
    h += st.turn + dTurn * st.d;
    x += Math.cos(h) * st.d; z += Math.sin(h) * st.d; alt += st.dz + dAlt * st.d; s += st.d;
    raw.push([x, z, alt, s]);
  }
  const [ex, ez] = [raw[raw.length - 1][0], raw[raw.length - 1][1]];
  // position closure: offset proportional to the distance covered, then downsampling (~60 m)
  const every = Math.max(1, Math.round(60 / STEP)), pts: number[][] = [];
  for (let i = 0; i < raw.length - 1; i += every) { const [px, pz, pa, ps] = raw[i], f = ps / L; pts.push([px - ex * f, pz - ez * f, pa]); }
  const minAlt = Math.min(...pts.map(p => p[2]));
  return pts.map(p => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10, Math.round((p[2] - minAlt) * 10) / 10]);
}

function segmentsCross(a: number[], b: number[], c: number[], d: number[]): boolean {
  const o = (p: number[], q: number[], r: number[]) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
}

/** Checks on a course: length, gradients, crossings, closing gap. Errors block publication. */
export function checkCourse(segs: CourseSegment[]): CourseIssue[] {
  const out: CourseIssue[] = [];
  const L = segs.reduce((a, s) => a + s.len, 0);
  if (L < 800) out.push({ level: 'error', message: `Loop too short (${Math.round(L)} m, minimum 800 m)` });
  if (L > 25000) out.push({ level: 'error', message: `Loop too long (${(L / 1000).toFixed(1)} km, maximum 25 km)` });
  const turn = segs.reduce((a, s) => a + s.turn, 0), off = Math.abs(turn - 360 * (Math.round(turn / 360) || 1));
  if (off > 90) out.push({ level: 'warning', message: `The corners add up to ${Math.round(turn)}°: a loop is ±360°, the course is heavily corrected` });
  for (const [i, s] of segs.entries()) if (Math.abs(s.turn) > 0 && s.len / ((Math.abs(s.turn) * Math.PI) / 180) < 12) out.push({ level: 'warning', message: `Segment ${i + 1}: very tight corner (radius < 12 m)` });
  const pts = courseToPoints(segs);
  if (pts.length >= 4) {
    for (let i = 0; i < pts.length; i++) for (let j = i + 2; j < pts.length; j++) {
      if (i === 0 && j === pts.length - 1) continue;
      if (segmentsCross(pts[i], pts[(i + 1) % pts.length], pts[j], pts[(j + 1) % pts.length])) { out.push({ level: 'error', message: 'The course crosses itself: adjust the corners or the lengths' }); i = pts.length; break; }
    }
    const tr = buildTrack({ name: 'check', pts }), f = trackFacts(tr);
    if (f.maxGrade > 0.2) out.push({ level: 'error', message: `Maximum gradient of ${(f.maxGrade * 100).toFixed(1)}%: 20% at most` });
    if (f.minGrade < -0.2) out.push({ level: 'error', message: `Descent of ${(-f.minGrade * 100).toFixed(1)}%: 20% at most` });
  }
  return out;
}
